import { errorMessage, SubmissionQuery } from '../../lead-forms/api'
import {
	submissionRow,
	ReportSubmissionRow
} from '../../lead-forms/reporting-adapter'
import { useEffect, useMemo, useRef, useState } from 'react'
import {
	AppButton,
	Tooltip,
	DotMatrix,
	CountBadge,
	Divider,
	Flag,
	Input,
	IntegrationLogo,
	Label,
	ListItem,
	Pagination,
	TwoPaneMenu,
	useEnterExit
} from '@clickguard/design-system'
import type {
	IntegrationProvider,
	TwoPaneMenuCategory
} from '@clickguard/design-system'
import Download01 from '@untitled-ui/icons-react/build/esm/Download01'
import FilterLines from '@untitled-ui/icons-react/build/esm/FilterLines'
import Globe02 from '@untitled-ui/icons-react/build/esm/Globe02'
import RefreshCw01 from '@untitled-ui/icons-react/build/esm/RefreshCw01'
import SearchMd from '@untitled-ui/icons-react/build/esm/SearchMd'
import { GTable } from './form-table'
import { GTableColumn } from './form-table/types'
import {
	EMAIL_OUTCOMES,
	useFormSpam,
	FormRecord,
	PAGE_SIZE_OPTIONS,
	PLACES,
	SubmissionRow,
	TRAFFIC_SOURCES,
	daysInRange,
	emailOutcome,
	getAllSubmissions,
	useFlowHeight,
	useSwapKey
} from '../form-spam-data'
import type { DashboardRange } from './traffic-quality-tray'

/**
 * R298 (Gustavo, 11 Sep, pin #100): the Submissions tab of the Lead Forms card.
 *
 * One table of every form's submissions, newest first. The per-form feed that
 * lived inside the detail drawer moved here and gained a Form column, a Form
 * filter group and an Export button. The range comes from the page header's
 * 7/30 Days control, the same one that cuts the forms table's counts.
 */

// A global table opens at 10 rows, twice the forms table's 5: it lists every
// form's rows, so a page of 5 would turn on almost every glance.
const PAGE_SIZE = 10

/** How many calendar days each range reads. Mirrors RANGE_TABS on the page. */
const RANGE_DAYS: Record<DashboardRange, number> = { '7d': 7, '30d': 30 }

// The Filters menu is the DS TwoPaneMenu facet mode, one facet per categorical
// column of this table. The Form facet is built per render from the forms
// list; these three are fixed vocabularies.
// R314 (Gustavo, 11 Sep): "ad platform logo always next to ad platforms when
// we're using them in tables, drop-down, etc." The DS `IntegrationLogo` knows
// the three ad networks; `Other` gets a neutral globe of the same 16px box so
// the text column stays aligned down the rows.
const SOURCE_PROVIDER: Partial<Record<string, IntegrationProvider>> = {
	'Google Ads': 'google-ads',
	'Meta Ads': 'meta',
	'Microsoft Ads': 'microsoft'
}

const SourceMark = ({ source }: { source: string }) => {
	const provider = SOURCE_PROVIDER[source]
	return provider ? (
		<IntegrationLogo
			provider={provider}
			size={16}
			decorative
			className="shrink-0"
		/>
	) : (
		<Globe02
			aria-hidden="true"
			className="h-4 w-4 shrink-0 cg-text-text-tertiary"
		/>
	)
}

const FIXED_CATEGORIES: TwoPaneMenuCategory[] = [
	{
		id: 'deliverability',
		label: 'Deliverability',
		items: EMAIL_OUTCOMES.map((o) => ({ id: o.key, label: o.label }))
	},
	{
		id: 'source',
		label: 'Traffic Source',
		items: TRAFFIC_SOURCES.map((s) => ({
			id: s,
			label: (
				<span className="flex items-center gap-2">
					<SourceMark source={s} />
					<span>{s}</span>
				</span>
			),
			searchText: s
		}))
	},
	{
		id: 'location',
		label: 'Location',
		// R314 (Gustavo, 11 Sep): "country actual flag not emoji". The DS `Flag`
		// draws the image from the place's ISO code (R316 data).
		items: PLACES.map(({ location, country }) => ({
			id: location,
			label: (
				<span className="flex items-center gap-2">
					<Flag code={country} size={16} decorative className="shrink-0" />
					<span>{location}</span>
				</span>
			),
			searchText: location
		}))
	}
]

/** The row value each filter category matches its picked ids against. */
const FACET_VALUE: Record<string, (row: SubmissionRow) => string> = {
	form: (row) => row.formId,
	deliverability: (row) => row.outcome,
	source: (row) => row.source,
	location: (row) => row.location
}

/** One CSV field: always quoted, inner quotes doubled. */
const csvField = (value: string) => `"${value.replace(/"/g, '""')}"`

export interface SubmissionsTableProps {
	forms: FormRecord[]
	/** The page header's 7/30 Days pick. */
	range: DashboardRange
	/**
	 * Form ids to seed the Form facet with. Read once, on mount: the drawer's
	 * "View submissions" link lands here with its own form picked, and the
	 * table owns the picks after that.
	 */
	initialFormIds?: string[]
}

export const SubmissionsTable = ({
	forms,
	range,
	initialFormIds
}: SubmissionsTableProps) => {
	const { api } = useFormSpam()
	// Facet picks from the Filters menu, keyed by category id.
	const [filters, setFilters] = useState<Record<string, string[]>>(
		(): Record<string, string[]> =>
			initialFormIds?.length ? { form: initialFormIds } : {}
	)
	const [skip, setSkip] = useState(0)
	const [pageSize, setPageSize] = useState(PAGE_SIZE)
	const [feedSort, setFeedSort] = useState('')
	// Pin #96 (Gustavo, 10 Sep): a search on email or IP, in place of the quick
	// filters. Narrows after the range and the facets; the CSV follows.
	const [search, setSearch] = useState('')
	// R314 (Gustavo, 11 Sep): "Reload button next to export on the table when
	// submissions tab is active". The tick rides `feedKey`, so a refresh replays
	// the same swap the forms tab's Refresh does (R300 `tableKey`).
	const [refreshTick, setRefreshTick] = useState(0)
	const [result, setResult] = useState<{
		rows: ReportSubmissionRow[]
		locations?: string[]
		total: number
	}>({ rows: [], total: 0 })
	const [loading, setLoading] = useState(false)
	const [error, setError] = useState('')
	const [exporting, setExporting] = useState(false)
	const [queryText, setQueryText] = useState('')
	useEffect(() => {
		const timer = window.setTimeout(() => {
			setQueryText(search)
			setSkip(0)
		}, 250)
		return () => window.clearTimeout(timer)
	}, [search])
	const sortKeys: Record<string, string> = {
		time: 'date',
		form: 'form_name',
		status: 'email_outcome',
		email: 'email',
		ip: 'client_ip',
		source: 'source',
		location: 'location'
	}
	const query: SubmissionQuery = {
		days: range === '7d' ? 7 : 30,
		page: Math.floor(skip / pageSize) + 1,
		limit: pageSize,
		query: queryText || undefined,
		sort_by: sortKeys[feedSort.replace(/^-/, '')] || 'date',
		sort: !feedSort || feedSort.startsWith('-') ? 'desc' : 'asc',
		...{
			form_ids: filters.form,
			email_outcomes: filters.deliverability?.map((key) =>
				key === 'spamTrap' ? 'spamTraps' : key
			),
			sources: filters.source,
			locations: filters.location
		}
	}
	const queryKey = JSON.stringify(query)
	useEffect(() => {
		if (!api) return
		let current = true
		setLoading(true)
		setError('')
		api
			.submissions(JSON.parse(queryKey))
			.then((data) => {
				if (current)
					setResult({
						rows: data.rows.map(submissionRow),
						total: data.total,
						locations: data.facets?.locations
					})
			})
			.catch((e) => {
				if (current) {
					setError(errorMessage(e))
					setResult((previous) => ({ ...previous, rows: [], total: 0 }))
				}
			})
			.finally(() => {
				if (current) setLoading(false)
			})
		return () => {
			current = false
		}
	}, [api, queryKey, refreshTick, forms])

	// The Form column and the CSV read the owner form by id.
	const formsById = useMemo(
		() => new Map(forms.map((form) => [form.id, form] as const)),
		[forms]
	)

	// The Form facet lists every linked form by name. It sits first: it is the
	// one facet the forms table cannot offer, and the drawer's deep link picks
	// in it.
	const categories = useMemo<TwoPaneMenuCategory[]>(
		() => [
			{
				id: 'form',
				label: 'Form',
				items: [...forms]
					.sort((a, b) => a.name.localeCompare(b.name))
					.map((form) => ({ id: form.id, label: form.name }))
			},
			...FIXED_CATEGORIES.map((category) =>
				api && category.id === 'location'
					? {
							id: category.id,
							label: category.label,
							items: Array.from(
								new Set([
									...(result.locations ||
										result.rows.map((row) => row.location)),
									...(filters.location || [])
								])
							)
								.filter((value) => value && value !== '—')
								.map((value) => ({ id: value, label: value }))
					  }
					: category
			)
		],
		[forms, api, result.rows, result.locations, filters.location]
	)

	const all = useMemo(
		() => (api ? result.rows : getAllSubmissions(forms)),
		[api, result.rows, forms]
	)
	// The range cut comes before the facets, so the empty-state copy can tell
	// "nothing in this range" from "nothing at all".
	const inRange = useMemo(() => {
		if (api) return all
		const days = daysInRange(RANGE_DAYS[range])
		return all.filter((row) => days.has(row.day))
	}, [all, range])
	// The Filters menu narrows by the facets. No picks in a facet = every
	// value of that facet. The search narrows last, on the email or the IP.
	const feedRows = useMemo(() => {
		if (api) return all
		const q = search.trim().toLowerCase()
		return inRange.filter(
			(row) =>
				Object.entries(filters).every(([id, picked]) => {
					if (!picked?.length) return true
					const value = FACET_VALUE[id]?.(row)
					return value !== undefined && picked.includes(value)
				}) &&
				(!q || row.email.toLowerCase().includes(q) || row.ip.includes(q))
		)
	}, [api, all, inRange, filters, search])
	const sortedFeed = useMemo(() => {
		if (api || !feedSort) return feedRows
		const key = feedSort.startsWith('-') ? feedSort.slice(1) : feedSort
		const dir = feedSort.startsWith('-') ? -1 : 1
		const rank = (row: SubmissionRow): string | number => {
			if (key === 'time') return Date.parse(`${row.day} 2026 ${row.clock}`)
			if (key === 'form') return formsById.get(row.formId)?.name ?? ''
			// R104: the outcome, in the legend's own order, so a sort walks the
			// stack from Valid down to Unknown. Alphabetical would scatter the
			// seven names and read as noise beside the chart.
			if (key === 'status')
				return EMAIL_OUTCOMES.findIndex((o) => o.key === row.outcome)
			if (key === 'email') return row.email
			if (key === 'ip') return row.ip
			if (key === 'source') return row.source
			return row.location
		}
		return [...feedRows].sort((a, b) => {
			const va = rank(a)
			const vb = rank(b)
			if (typeof va === 'string' && typeof vb === 'string')
				return va.localeCompare(vb) * dir
			return ((va as number) - (vb as number)) * dir
		})
	}, [api, feedRows, feedSort, formsById])
	// GTable renders the page it is handed. It does not slice, so the parent does.
	const page = api ? sortedFeed : sortedFeed.slice(skip, skip + pageSize)
	const resultTotal = api ? result.total : feedRows.length

	// The range is a prop, so its reset is an effect; the filter, sort and
	// page-size handlers reset in place. On mount skip is already 0.
	useEffect(() => {
		setSkip(0)
	}, [range])

	// The row set can shrink under a nonzero skip (a narrower range, a form
	// unlinked in the drawer). The slice then answers empty, and GTable hides
	// the pager on an empty table, so nothing on screen can bring the user back.
	useEffect(() => {
		if (!loading && skip > 0 && skip >= resultTotal) setSkip(0)
	}, [resultTotal, skip, loading])

	// The empty state names the search when it is the search that emptied the view.
	const searching = search.trim().length > 0
	const activeFilters = Object.values(filters).reduce(
		(total, ids) => total + ids.length,
		0
	)

	// Same badge enter/exit as the forms-table trigger: the rightIcon prop
	// unmounts with the badge (a null-rendering badge still mounts AppButton's
	// 16px slot), and the last positive count is held for the exit.
	const filterBadge = useEnterExit(activeFilters > 0, { duration: 150 })
	const lastFilterCount = useRef(1)
	useEffect(() => {
		if (activeFilters > 0) lastFilterCount.current = activeFilters
	}, [activeFilters])

	// The key names every input that changes the SET of rows: the picks, the
	// range and the page size. The sort and the page turn are not in it; they
	// reorder or slice the same set and must not replay the fade.
	const feedKey = `${categories
		.map((category) => (filters[category.id] ?? []).join('+'))
		.join('|')}-${range}-${pageSize}-${refreshTick}`

	// The table swaps exactly like the forms table on the other tab.
	//
	// `useSwapKey` lags the painted key by one 150ms exit, so the old rows get
	// a real exit. `page` is already the NEW slice on the render that starts
	// the exit, so `lastFeed` holds what actually committed and the table
	// reads that copy while the exit plays. `useFlowHeight` eases the box to
	// the height the new rows really take, so the card under it does not jump.
	const { painted: paintedFeedKey, leaving: feedLeaving } = useSwapKey(feedKey)
	const { inner: swapInner, height: swapHeight } = useFlowHeight()
	const lastFeed = useRef({ page, total: resultTotal })
	useEffect(() => {
		if (!feedLeaving) lastFeed.current = { page, total: resultTotal }
	})
	const shownFeed = feedLeaving ? lastFeed.current.page : page
	const shownFeedTotal = feedLeaving ? lastFeed.current.total : resultTotal
	// The footer is the DS `Pagination`, the same one the forms table renders.
	// GTable's own footer is off; the page number derives from `skip`. Held
	// through a 150ms fade when a view empties.
	const feedTotalPages = Math.max(1, Math.ceil(shownFeedTotal / pageSize))
	const feedPage = Math.min(Math.floor(skip / pageSize) + 1, feedTotalPages)
	const feedPager = useEnterExit(shownFeedTotal > 0, { duration: 150 })

	// Prototype: the client builds the file from the rows it already holds,
	// every filtered and sorted row in the range, not just the page on screen.
	// Production would stream the same columns from the API.
	const exportCsv = async () => {
		if (api) {
			setExporting(true)
			setError('')
			try {
				const blob = await api.export(query)
				const url = URL.createObjectURL(blob)
				const link = document.createElement('a')
				link.href = url
				link.download = 'lead-form-submissions.csv'
				link.click()
				setTimeout(() => URL.revokeObjectURL(url), 1000)
			} catch (e) {
				setError(errorMessage(e))
			} finally {
				setExporting(false)
			}
			return
		}
		const header = [
			'Date',
			'Form',
			'Form URL',
			'Email',
			'Traffic Source',
			'Location',
			'IP address',
			'Deliverability'
		]
		const lines = sortedFeed.map((row) => {
			const form = formsById.get(row.formId)
			return [
				`${row.day} 2026, ${row.clock}`,
				form?.name ?? '',
				form?.url ?? '',
				row.email,
				row.source,
				row.location,
				row.ip,
				emailOutcome(row.outcome).label
			]
		})
		const text = [header, ...lines]
			.map((fields) => fields.map(csvField).join(','))
			.join('\r\n')
		const blob = new Blob([text], { type: 'text/csv;charset=utf-8' })
		const url = URL.createObjectURL(blob)
		const link = document.createElement('a')
		link.href = url
		link.download = `lead-form-submissions-${range}-${new Date()
			.toISOString()
			.slice(0, 10)}.csv`
		document.body.appendChild(link)
		link.click()
		link.remove()
		URL.revokeObjectURL(url)
	}

	const columns = useMemo<GTableColumn<SubmissionRow>[]>(
		() => [
			{
				// R295 (Gustavo, 10 Sep, pin #96): the head reads `Date`, not `Time`.
				label: 'Date',
				sortKey: 'time',
				className: 'justify-start',
				// Pinned left while this table scrolls, like the forms table.
				freeze: true,
				// R83: every column states its width. The table is `table-layout:
				// fixed`, and the virtualiser mounts the rows a few at a time, so a
				// column with no width is re-divided from whatever rows are on
				// screen and the cells change width while the user scrolls. Each
				// width was measured over every form, so each holds its widest
				// content with a pixel or two to spare.
				//
				// R103: the Email column joined as the second one, R104 widened
				// Status, and R113 and R115 gave 56px back. R295 (Gustavo, 10 Sep,
				// pin #96): Traffic Source joined, Time on page and Network left.
				// R298: the Form column joined at 240px, so the seven widths add up
				// to 1284px, past the card at most window widths, and the row
				// scrolls sideways. That is what `freeze` on this column and
				// `freezeRight` on Deliverability were built for: the date anchors
				// the row on the left, the verdict anchors it on the right, and the
				// GTable draws the scroll shadow on both.
				width: '145px',
				parentClassName: 'z-10 cg-bg-canvas whitespace-nowrap',
				render: (row) => (
					<span className="whitespace-nowrap text-[14px] leading-[18px] cg-text-text-primary">
						{row.day}, {row.clock}
					</span>
				)
			},
			{
				// R298 (Gustavo, 11 Sep, pin #100): which form the row came in
				// through. This is the forms table's Form cell, name over URL, so a
				// reader who knows that table recognises the form here at a glance.
				label: 'Form',
				sortKey: 'form',
				className: 'justify-start',
				// 240px leaves 192px for the text after the 24px cell padding. The
				// forms table gives its own Form cell 264px because it is the only
				// pinned column there; here Date already anchors the left, so the
				// name and the domain of every seed URL fit and only the long paths
				// truncate, the balance R275 settled on.
				width: '240px',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (row) => {
					const form = formsById.get(row.formId)
					return (
						<div className="flex min-w-0 flex-col">
							<span className="truncate text-[14px] font-medium leading-[18px] cg-text-text-primary">
								{form?.name ??
									(row as ReportSubmissionRow).formName ??
									'Unlinked form'}
							</span>
							<div className="min-w-0 [&>div]:max-w-full">
								<Tooltip content={form?.url || (row as ReportSubmissionRow).formUrl} portal>
									<span className="truncate text-[14px] leading-[18px] cg-text-text-secondary">
										{form?.url ?? (row as ReportSubmissionRow).formUrl ?? ''}
									</span>
								</Tooltip>
							</div>
						</div>
					)
				}
			},
			{
				label: 'Email',
				sortKey: 'email',
				className: 'justify-start',
				// R115, at his ask ("these cells seem to be too wide, I'd make 'em
				// shorter and long texts can truncate"): 248px, down from 300px.
				//
				// R104 sized this column to the LONGEST address so nothing ever
				// truncated. That was the wrong trade. The 25 addresses run from
				// 133px to 230px of glyphs at 14px host-grotesk, and the median is
				// 181px, so sizing to 230px paid 50px of white on every row to keep
				// three bot addresses whole.
				//
				// 248px spends 48px on padding and leaves a 200px content box. That
				// cuts 5 of the 25: the three `mailtrap-throwaway.top` addresses,
				// `amara.boyd@fernwoodgroup.com` and `d.whitfield+forms@northpine.co`.
				// The other 20 sit whole.
				//
				// 200px and not 195px because the pool has a 12.6px gap there: the
				// widest address that still fits is 190.5px and the narrowest that
				// truncates is 203.1px. The boundary sits in the middle of that gap,
				// so a font that reflows by a pixel or two cannot change which rows
				// truncate.
				//
				// The local part is what identifies a submitter, and it survives in
				// all 25. Only the domain tail ever goes, and the design-system tooltip
				// carries the whole address, the same way Location does.
				width: '248px',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (row) => (
					<div className="min-w-0 [&>div]:max-w-full">
						<Tooltip content={row.email} portal>
							<span className="block truncate text-[14px] leading-[18px] cg-text-text-primary">
								{row.email}
							</span>
						</Tooltip>
					</div>
				)
			},
			{
				// R295 (Gustavo, 10 Sep, pin #96): the ad network the visitor came
				// from, one of Google Ads, Meta Ads, Microsoft Ads, Other.
				label: 'Traffic Source',
				sortKey: 'source',
				className: 'justify-start',
				// Measured on the running build at 14px host-grotesk: the widest
				// value, `Microsoft Ads`, draws 88.6px, plus the R314 16px logo and
				// its 8px gap = 112.6px, plus 24px of padding each side = 160.6px.
				// The header (`Traffic Source` plus the sort glyph, 143.5px padded)
				// no longer sets the floor. One 4px step of room: 164px.
				width: '164px',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (row) => (
					<span className="flex items-center gap-2 whitespace-nowrap text-[14px] leading-[18px] cg-text-text-primary">
						<SourceMark source={row.source} />
						<span>{row.source}</span>
					</span>
				)
			},
			{
				label: 'Location',
				sortKey: 'location',
				className: 'justify-start',
				width: '194px',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (row) => (
					<span className="flex min-w-0 items-center gap-2 whitespace-nowrap text-[14px] leading-[18px] cg-text-text-primary">
						<Flag
							code={row.country}
							size={16}
							decorative
							className="shrink-0"
						/>
						<div className="min-w-0 [&>div]:max-w-full">
							<Tooltip content={row.location} portal>
								<span className="truncate">{row.location}</span>
							</Tooltip>
						</div>
					</span>
				)
			},
			{
				label: 'IP address',
				sortKey: 'ip',
				className: 'justify-start',
				width: '169px',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				render: (row) => (
					<span className="text-[14px] leading-[18px] cg-text-text-secondary">
						{row.ip}
					</span>
				)
			},
			{
				// R293 (Gustavo, 4 Sep, pin #94): the column carries the same word as
				// the legend head over the chart, Deliverability, not Status.
				// R295 (Gustavo, 10 Sep, pin #96; Esteban, 11 Sep): the column pins
				// on the right edge. Gustavo's list put it first; Esteban put it at
				// the right end.
				label: 'Deliverability',
				sortKey: 'status',
				className: 'justify-start',
				freezeRight: true,
				// R104: the pill names the outcome now, so the column must hold
				// `Do not mail` and not just `Blocked`. R113, at his ask ("the
				// status cells are probably a bit wide"): the column now sits ON
				// the floor instead of 15px above it.
				//
				// Re-measured on the running build at 14px host-grotesk: the widest
				// of the seven pills, `Do not mail`, draws 76.8px, and the cell
				// spends 24px of padding on each side. 76.8 + 48 = 124.8, so the
				// floor is 125px.
				//
				// 128px, and not the floor, because 125px leaves `Do not mail`
				// 0.2px of room. The pill is `shrink-0`, so a font that reflows
				// by one pixel would not cut the word, it would push the pill
				// into the 24px gutter and crowd the IP address beside it. One
				// 4px step of room costs nothing and removes that.
				//
				// R293: the header reads `Deliverability` now, 90px with the sort
				// glyph and 138px with the padding, so the header sets the floor and
				// the column takes the same one step of room: 140px.
				width: '140px',
				parentClassName: 'cg-bg-canvas whitespace-nowrap',
				/* R104, at his ask: "The status row should show the 'valid',
				   'alias, etc things that we have in the outcomes."

				   The chart names seven outcomes and the table used to name three
				   verdicts, so the same submission was described twice in two
				   vocabularies. Now the row speaks the chart's words.

				   The colour still carries the verdict, because the DS Label has
				   five colours and the layer has seven outcomes. So the reader sees
				   green, amber or red at a glance, and reads one word to learn why.
				   `Unknown` takes gray: we did not decide, so no verdict colour
				   would be honest. */
				render: (row) => {
					const out = emailOutcome(row.outcome)
					return (
						<Label size="sm" color={out.tone}>
							{out.label}
						</Label>
					)
				}
			}
		],
		[formsById]
	)

	return (
		<>
			<div className="flex items-center gap-2.5">
				{/* [&_span]: pulls the DS leading-icon span's 14px pad back to 6px;
				    !border matches the toolbar buttons (twMerge can't dedupe cg-). */}
				<div className="w-[200px] [&_span[aria-hidden]]:!pl-1.5">
					<Input
						size="sm"
						state="default"
						className="[&:not(:focus-within)]:!border-[rgb(var(--cg-border))]"
						// 6px vertical padding lands the field at 32px, flush with the sm AppButtons.
						style={{ paddingTop: 6, paddingBottom: 6 }}
						placeholder="Search email or IP…"
						aria-label="Search submissions by email or IP address"
						// 14px, not the DS default 16px: at 32px the glyph reads heavier than the text.
						leadingIcon={<SearchMd className="h-3.5 w-3.5" />}
						autoComplete="off"
						value={search}
						onChange={(e) => {
							setSearch(e.target.value)
							setSkip(0)
						}}
					/>
				</div>
				<TwoPaneMenu
					className="form-spam-facet"
					selectionMode="multiple"
					twoTone
					showSearch={false}
					categories={categories}
					selectedItemIds={filters}
					onSelectionChange={(next) => {
						setFilters(next)
						setSkip(0)
					}}
					categoriesFooter={
						<div className="px-2 pb-2">
							<Divider />
							<ListItem
								as="button"
								// 8px, to match the 8px the pane's own p-2 puts ABOVE the
								// rule. mt-1 gave 4, so the rule sat closer to Clear filters
								// than to the last filter. Same space each side of a rule.
								className="mt-2"
								label={<span className="cg-text-brand">Clear filters</span>}
								disabled={activeFilters === 0}
								onClick={() => {
									setFilters({})
									setSkip(0)
								}}
							/>
						</div>
					}
					trigger={
						<AppButton
							variant="secondary"
							size="sm"
							label="Filters"
							leftIcon={<FilterLines className="h-4 w-4" />}
							rightIcon={
								filterBadge.mounted ? (
									<CountBadge
										count={
											activeFilters > 0
												? activeFilters
												: lastFilterCount.current
										}
										className={`transition duration-150 motion-reduce:transition-none ${
											filterBadge.entered
												? 'scale-100 opacity-100'
												: 'scale-75 opacity-0'
										}`}
									/>
								) : undefined
							}
						/>
					}
				/>
				<div className="flex-1" />
				{/* R298 (Gustavo, 11 Sep, pin #100): the export takes the rows the
				    reader has narrowed to, so a filtered view downloads as it reads.
				    Off when there is nothing to write. */}
				<AppButton
					variant="secondary"
					size="sm"
					label="Refresh"
					leftIcon={<RefreshCw01 className="h-4 w-4" />}
					onClick={() => {
						setSkip(0)
						setRefreshTick((tick) => tick + 1)
					}}
				/>
				<AppButton
					variant="secondary"
					size="sm"
					label="Export"
					leftIcon={
						exporting ? (
							<DotMatrix size="xs" />
						) : (
							<Download01 className="h-4 w-4" />
						)
					}
					disabled={resultTotal === 0 || loading || exporting}
					onClick={exportCsv}
				/>
			</div>

			{error && (
				<p role="alert" className="px-4 cg-text-danger">
					{error}
				</p>
			)}
			<div
				// The height is inline and it follows the measured contents.
				// Hide the overflow with it: a shorter box must clip the rows
				// it is losing, not paint them past its own edge.
				style={swapHeight === undefined ? undefined : { height: swapHeight }}
				className="form-spam-swap-frame overflow-hidden"
			>
				{/* The measured element. It has to be a wrapper and not the keyed
				    panel itself, because the panel is a NEW element on every swap
				    and an observer on it would go with the old one. */}
				<div ref={swapInner}>
					<div
						key={paintedFeedKey}
						// `--still` drops the 4px rise both keyframes carry. The frame
						// around this one is already easing its own height, and two
						// movements at once read as a lurch, not as one swap.
						className={`overflow-hidden rounded-t-lg ${
							feedLeaving
								? 'form-spam-leave form-spam-leave--still'
								: 'form-spam-panel form-spam-panel--still'
						}`}
					>
						{loading ? (
							<div
								role="status"
								aria-label="Loading submissions"
								className="flex justify-center p-12"
							>
								<DotMatrix />
							</div>
						) : (
							<GTable
								variant="ds"
								containerClassName="rounded-none bg-transparent"
								columns={columns}
								// The held set, not the live one. The exit must fade
								// the rows the reader was looking at.
								data={shownFeed}
								getRowId={(row) => row.id}
								sort={feedSort}
								sortListener={(key) => {
									setFeedSort(key)
									setSkip(0)
								}}
								totalCount={shownFeedTotal}
								// The footer is the DS Pagination below, not GTable's.
								showPagination={false}
								emptyState={
									<div className="flex flex-col items-center gap-1 py-10 text-center">
										<span className="text-[14px] font-medium leading-[18px] cg-text-text-primary">
											{all.length === 0
												? 'No submissions yet'
												: searching && feedRows.length === 0
												? 'No submissions match this search'
												: 'No submissions match this view'}
										</span>
										<span className="text-[13px] leading-[18px] cg-text-text-secondary">
											{all.length === 0
												? 'Rows appear on the first submission to a linked form.'
												: searching && feedRows.length === 0
												? 'Try another email or IP address, or clear the search.'
												: 'Clear the filters, or pick a wider range.'}
										</span>
									</div>
								}
							/>
						)}
						{/* The canonical footer sits in the keyed panel, so it swaps
						    with the rows. Held through its 150ms exit when the view
						    empties. */}
						{!loading && feedPager.mounted ? (
							<div
								className={`transition-opacity duration-150 motion-reduce:transition-none ${
									feedPager.entered ? 'opacity-100' : 'opacity-0'
								}`}
							>
								<Pagination
									page={feedPage}
									totalPages={feedTotalPages}
									onPageChange={(next) => setSkip((next - 1) * pageSize)}
									pageSize={pageSize}
									pageSizeOptions={PAGE_SIZE_OPTIONS}
									onPageSizeChange={(next) => {
										setPageSize(next)
										setSkip(0)
									}}
								/>
							</div>
						) : null}
					</div>
				</div>
			</div>
		</>
	)
}
